import { useEffect, useMemo, useState } from "react";
import { ClipboardCheck, Clock3, FilePlus2, Send } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { StatusPill } from "@/components/StatusPill";
import { AutomationBanner } from "@/components/AutomationBanner";
import { apiClient } from "@/lib/api";

const sectionTitle = (questionId) => {
  if (["fullname", "discord_contact"].includes(questionId)) return "Dane podstawowe";
  if (["experience", "motivation", "strengths", "weaknesses"].includes(questionId)) return "Doświadczenie i przygotowanie Dev";
  if (["availability", "scenarios", "rules"].includes(questionId)) return "Dyspozycyjność i praca zespołowa";
  return "Dodatkowe informacje";
};

export default function ApplicantPage({ authData, refreshAuth }) {
  const [questionSet, setQuestionSet] = useState(null);
  const [applications, setApplications] = useState([]);
  const [pendingExists, setPendingExists] = useState(false);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const loadApplicantData = async () => {
    try {
      setLoading(true);
      const [questionsResponse, applicationsResponse] = await Promise.all([
        apiClient.get("/questions/active"),
        apiClient.get("/applications/me"),
      ]);
      const nextQuestionSet = questionsResponse.data.question_set;
      setQuestionSet(nextQuestionSet);
      setApplications(applicationsResponse.data.applications || []);
      setPendingExists(applicationsResponse.data.pending_exists);
      setAnswers((current) => {
        const seeded = { ...current };
        (nextQuestionSet?.questions || []).forEach((question) => {
          if (typeof seeded[question.id] !== "string") seeded[question.id] = "";
        });
        return seeded;
      });
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Nie udało się pobrać danych formularza.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadApplicantData();
  }, []);

  const groupedQuestions = useMemo(() => {
    const groups = {};
    (questionSet?.questions || []).forEach((question) => {
      const group = sectionTitle(question.id);
      groups[group] = groups[group] || [];
      groups[group].push(question);
    });
    return groups;
  }, [questionSet]);

  const latestApplication = applications[0] || authData?.latest_application;

  const submitApplication = async () => {
    setSubmitting(true);
    try {
      const payload = {
        answers: (questionSet?.questions || []).map((question) => ({
          question_id: question.id,
          question_label: question.label,
          answer: answers[question.id] || "",
        })),
      };
      await apiClient.post("/applications", payload);
      toast.success("Zgłoszenie zostało wysłane. Możesz wrócić później, aby sprawdzić status.");
      await Promise.all([loadApplicantData(), refreshAuth()]);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Nie udało się wysłać zgłoszenia.");
    } finally {
      setSubmitting(false);
    }
  };

  if (authData?.user?.can_review) {
    return (
      <Card className="border-border bg-card">
        <CardContent className="p-8 text-sm text-muted-foreground">
          To konto korzysta z wewnętrznego panelu obsługi zgłoszeń. Widok aplikanta jest ukryty.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <section className="grid gap-4 lg:grid-cols-[1.15fr_0.85fr]">
        <Card className="surface-noise border-border bg-card shadow-[0_10px_30px_rgba(0,0,0,0.35)]">
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-xs uppercase tracking-[0.24em] text-primary/80">Byteflow</p>
                <CardTitle className="mt-2 text-3xl font-semibold tracking-tight">Junior Dev Operative</CardTitle>
              </div>
              <div className="rounded-2xl border border-primary/20 bg-primary/10 px-4 py-3 text-right">
                <p className="text-xs uppercase tracking-[0.24em] text-primary/80">Wersja formularza</p>
                <p className="mt-1 text-lg font-semibold">v{questionSet?.version || 1}</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-muted-foreground">
            <p>
              Wypełnij formularz dokładnie. W danym momencie możesz posiadać jedno aktywne zgłoszenie oczekujące na wynik.
            </p>
            {pendingExists ? (
              <Alert className="border-[hsl(38_60%_28%)] bg-[hsl(38_30%_14%)]" data-testid="applicant-status-card">
                <AlertTitle className="text-[hsl(38_92%_70%)]">Masz już aktywne zgłoszenie</AlertTitle>
                <AlertDescription className="text-[hsl(38_92%_78%)]/90">
                  Poczekaj na aktualizację statusu, zanim wyślesz kolejne zgłoszenie.
                </AlertDescription>
              </Alert>
            ) : null}
          </CardContent>
        </Card>

        <div className="space-y-4" id="status-overview-section">
          <Card className="border-border bg-card">
            <CardContent className="p-5">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-[0.24em] text-primary/80">Status zgłoszenia</p>
                  <h2 className="mt-2 text-lg font-semibold">Twoje ostatnie zgłoszenie</h2>
                </div>
                <StatusPill status={latestApplication?.status || "not_started"} data-testid="applicant-status-card" />
              </div>
              <div className="mt-4 space-y-3 text-sm text-muted-foreground">
                <div className="flex items-center gap-2"><ClipboardCheck className="h-4 w-4 text-primary" /> Wszystkie aktualizacje zobaczysz w tym miejscu.</div>
                <div className="flex items-center gap-2"><Clock3 className="h-4 w-4 text-primary" /> Blokada nowego formularza działa tylko dla aktywnego zgłoszenia.</div>
                <div className="flex items-center gap-2"><FilePlus2 className="h-4 w-4 text-primary" /> Po zakończeniu procesu możesz wysłać nowe zgłoszenie, jeśli będzie taka potrzeba.</div>
              </div>
            </CardContent>
          </Card>

          {latestApplication?.status === "accepted" ? (
            <AutomationBanner provisioning={latestApplication.discord_provisioning} compact audience="public" />
          ) : null}
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
        <Card className="border-border bg-card shadow-[0_10px_30px_rgba(0,0,0,0.25)]">
          <CardHeader>
            <CardTitle>Formularz rekrutacyjny</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-4">
                <div className="h-12 rounded-2xl bg-muted" />
                <div className="h-40 rounded-2xl bg-muted" />
                <div className="h-40 rounded-2xl bg-muted" />
              </div>
            ) : (
              <form className="space-y-6" data-testid="application-form">
                {Object.entries(groupedQuestions).map(([groupLabel, questions]) => (
                  <Card key={groupLabel} className="border-border bg-background/65">
                    <CardHeader>
                      <CardTitle className="text-lg">{groupLabel}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-5">
                      {questions.map((question) => (
                        <div key={question.id} className="space-y-2">
                          <label className="text-sm font-medium text-foreground" htmlFor={`question-${question.id}`}>
                            {question.label} {question.required ? <span className="text-primary">*</span> : null}
                          </label>
                          <Textarea
                            id={`question-${question.id}`}
                            value={answers[question.id] || ""}
                            onChange={(event) => setAnswers((current) => ({ ...current, [question.id]: event.target.value }))}
                            disabled={pendingExists}
                            placeholder={question.placeholder}
                            className="min-h-[130px] resize-y bg-card"
                            data-testid={`question-${question.id}-input`}
                          />
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                ))}

                <div className="flex flex-wrap items-center gap-3">
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        type="button"
                        className="h-11"
                        disabled={pendingExists || submitting || loading}
                        data-testid="application-form-submit-button"
                      >
                        <Send className="mr-2 h-4 w-4" /> Wyślij zgłoszenie
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="border-border bg-card">
                      <AlertDialogHeader>
                        <AlertDialogTitle>Potwierdź wysyłkę zgłoszenia</AlertDialogTitle>
                        <AlertDialogDescription>
                          Po wysłaniu formularz zostanie zablokowany do czasu aktualizacji statusu.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Anuluj</AlertDialogCancel>
                        <AlertDialogAction onClick={submitApplication}>Potwierdź wysyłkę</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                  <p className="text-sm text-muted-foreground">{pendingExists ? "Formularz jest tymczasowo zablokowany." : "Sprawdź odpowiedzi przed wysłaniem."}</p>
                </div>
              </form>
            )}
          </CardContent>
        </Card>

        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle>Status zgłoszenia</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {applications.length ? (
              applications.map((application) => (
                <div key={application._id} className="rounded-2xl border border-border bg-background/65 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium text-foreground">Zgłoszenie #{application._id.slice(-6)}</p>
                      <p className="mt-1 text-xs text-muted-foreground">Wersja formularza v{application.question_set_version}</p>
                    </div>
                    <StatusPill status={application.status} />
                  </div>
                  {application.reviewer_note ? (
                    <p className="mt-3 text-sm leading-relaxed text-muted-foreground">Aktualizacja: {application.reviewer_note}</p>
                  ) : null}
                </div>
              ))
            ) : (
              <div className="rounded-2xl border border-dashed border-border bg-background/70 p-4 text-sm text-muted-foreground">
                Nie masz jeszcze zapisanych zgłoszeń.
              </div>
            )}
          </CardContent>
        </Card>
      </section>
    </div>
  );
}
